import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Github, Send } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { toast } from "sonner";

const LOGO_URL =
  "https://static.prod-images.emergentagent.com/jobs/981795dc-3f85-41a2-8218-f3db7d35fb62/images/33e54ddee2de433632b5f87b4e137bb11cce4f32e0d5989a55474dd8cf991f88.png";

function Newsletter() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { t } = useTranslation();

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const r = await api.post("/newsletter", { email });
      if (r.data.already_subscribed) {
        toast.info("Vous êtes déjà inscrit·e — merci !");
      } else {
        toast.success("Inscription confirmée. Bienvenue chez Nord Stella.");
      }
      setEmail("");
    } catch {
      toast.error("Email invalide");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="border-t border-white/10 bg-[#0A2A4F]" data-testid="newsletter-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#C5CDD8]">
            Newsletter
          </div>
          <h3 className="font-display mt-3 text-2xl sm:text-3xl font-bold uppercase tracking-tighter leading-[1.05] text-white">
            {t("footer.newsletter_title")}
          </h3>
          <p className="mt-3 text-sm text-white/60 max-w-md">
            {t("footer.newsletter_consent")}
          </p>
        </div>
        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3" data-testid="newsletter-form">
          <input
            type="email"
            required
            placeholder={t("footer.newsletter_placeholder")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 border border-white/30 bg-transparent text-white px-4 py-4 text-sm focus:outline-none focus:border-white"
            data-testid="newsletter-email"
          />
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 border border-white bg-white text-[#001F3F] hover:bg-transparent hover:text-white transition-all uppercase tracking-[0.25em] text-xs font-bold px-8 py-4 disabled:opacity-60"
            data-testid="newsletter-submit"
          >
            {submitting ? "..." : t("footer.newsletter_submit")}
            {!submitting && <Send size={14} />}
          </button>
        </form>
      </div>
    </section>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  return (
    <>
      <Newsletter />
      <footer className="bg-[#000B1A] text-white border-t border-white/10" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <img
              src={LOGO_URL}
              alt="Nord Stella Tech"
              className="h-16 w-auto"
            />
            <p className="mt-6 text-sm text-white/60 max-w-md leading-relaxed">
              {t("footer.tagline")}
            </p>
            <div className="flex gap-4 mt-6 text-white/60">
              <a href="#" className="hover:text-white" aria-label="Instagram">
                <Instagram size={18} strokeWidth={1.5} />
              </a>
              <a href="#" className="hover:text-white" aria-label="Twitter">
                <Twitter size={18} strokeWidth={1.5} />
              </a>
              <a href="#" className="hover:text-white" aria-label="GitHub">
                <Github size={18} strokeWidth={1.5} />
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-[#C5CDD8]">{t("footer.shop")}</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/shop" className="hover:text-white">{t("shop.filter_all")}</Link></li>
              <li><Link to="/shop?category=smart" className="hover:text-white">Smart Tech</Link></li>
              <li><Link to="/shop?category=wellness" className="hover:text-white">Home Office Wellness</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-[#C5CDD8]">{t("footer.company")}</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/about" className="hover:text-white">{t("nav.about")}</Link></li>
              <li><Link to="/contact" className="hover:text-white">{t("nav.contact")}</Link></li>
              <li><Link to="/tracking" className="hover:text-white" data-testid="footer-tracking">{t("nav.tracking")}</Link></li>
              <li><Link to="/shipping" className="hover:text-white" data-testid="footer-shipping">{t("product.shipping_info")}</Link></li>
              <li><Link to="/account" className="hover:text-white">{t("nav.account")}</Link></li>
              <li><Link to="/parrainage" className="hover:text-white text-[#FFD700]" data-testid="footer-parrainage">{t("referral.eyebrow")}</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.25em] mb-4 text-[#C5CDD8]">{t("footer.legal")}</h4>
            <ul className="space-y-2 text-sm text-white/70">
              <li><Link to="/legal/cgv" className="hover:text-white" data-testid="footer-cgv">{t("footer.cgv")}</Link></li>
              <li><Link to="/legal/mentions" className="hover:text-white" data-testid="footer-mentions">{t("footer.mentions")}</Link></li>
              <li><Link to="/legal/privacy" className="hover:text-white" data-testid="footer-privacy">{t("footer.privacy")}</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-white" data-testid="footer-cookies">{t("footer.cookies")}</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))}
                  className="hover:text-white text-left"
                  data-testid="footer-cookie-prefs"
                >
                  {t("footer.cookie_prefs")}
                </button>
              </li>
              <li className="pt-2">
                <a href="mailto:nordstellatech@gmail.com" className="hover:text-white text-[#C5CDD8] break-all" data-testid="footer-email">
                  nordstellatech@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between text-xs text-white/50 uppercase tracking-widest">
            <span>© 2026 Nord Stella Tech — {t("footer.rights")}</span>
            <span data-testid="footer-legal" className="md:text-center">
              SIRET : 104 860 663 00012 · TVA non applicable, art. 293 B du CGI
            </span>
            <span>{t("checkout.secure_payment")}</span>
          </div>
        </div>
      </footer>
    </>
  );
}
